import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import {
  contentApi,
  ContentListParams,
  ContentTransition,
  ContentItem,
  ContentType,
  ContentStatus,
  GenerateMockupsRequest,
  MockupArtwork,
  MockupSlot,
  BlogBrief,
} from '../api/contentApi'

const STATS_KEY = 'content-stats'
const ITEMS_KEY = 'content-items'
const ITEM_KEY = 'content-item'
const ACTIONS_KEY = 'content-actions'

export function useContentStats() {
  return useQuery({
    queryKey: [STATS_KEY],
    queryFn: () => contentApi.getStats(),
    refetchInterval: 20000,
  })
}

export function useContentItems(params: ContentListParams = {}) {
  return useQuery({
    queryKey: [ITEMS_KEY, params],
    queryFn: () => contentApi.getItems(params),
    refetchInterval: 15000,
  })
}

export function useContentItem(id: string | null) {
  return useQuery({
    queryKey: [ITEM_KEY, id],
    queryFn: () => contentApi.getItem(id!),
    enabled: !!id,
  })
}

export function useContentActions(id: string | null) {
  return useQuery({
    queryKey: [ACTIONS_KEY, id],
    queryFn: () => contentApi.getActions(id!),
    enabled: !!id,
  })
}

export function useCreateAcademyDraft() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: () => contentApi.createAcademyDraft(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ITEMS_KEY] })
      qc.invalidateQueries({ queryKey: [STATS_KEY] })
    },
    onError: (e: Error) => toast({ title: 'Could not create Academy draft', description: e.message, variant: 'destructive' }),
  })
}

export function useUpdateContentItem() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ContentItem> }) =>
      contentApi.updateItem(id, data),
    onSuccess: (item) => {
      qc.invalidateQueries({ queryKey: [ITEMS_KEY] })
      qc.invalidateQueries({ queryKey: [ITEM_KEY, item.id] })
      qc.invalidateQueries({ queryKey: [ACTIONS_KEY, item.id] })
      toast({ title: 'Saved' })
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  })
}

export function useTransitionContentItem() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, action, note }: { id: string; action: ContentTransition; note?: string }) =>
      contentApi.transition(id, action, note),
    onSuccess: (item, { action }) => {
      qc.invalidateQueries({ queryKey: [ITEMS_KEY] })
      qc.invalidateQueries({ queryKey: [ITEM_KEY, item.id] })
      qc.invalidateQueries({ queryKey: [ACTIONS_KEY, item.id] })
      qc.invalidateQueries({ queryKey: [STATS_KEY] })
      toast({ title: `Marked ${action}` })
    },
    onError: (e: Error) => toast({ title: 'Action failed', description: e.message, variant: 'destructive' }),
  })
}

// ─── Mockup generation ───────────────────────────────────
export function useGenerateMockups() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (req: GenerateMockupsRequest) => contentApi.generateMockups(req),
    onSuccess: (item) => {
      qc.invalidateQueries({ queryKey: [ITEMS_KEY] })
      qc.invalidateQueries({ queryKey: [STATS_KEY] })
      qc.invalidateQueries({ queryKey: [ITEM_KEY, item.id] })
      toast({ title: 'Mockups generated', description: 'Draft created — review and approve.' })
    },
    onError: (e: Error) => toast({ title: 'Generation failed', description: e.message, variant: 'destructive' }),
  })
}

// Render-target registry (surfaces) for the generate dialog.
export function useMockupTargets() {
  return useQuery({
    queryKey: ['mockup-targets'],
    queryFn: () => contentApi.getMockupTargets(),
    staleTime: 5 * 60 * 1000,
  })
}

// Regenerate / add a single slot. Returns the slide; the caller merges it into payload.
export function useGenerateMockupSlot() {
  const { toast } = useToast()
  return useMutation({
    mutationFn: (req: { artwork: MockupArtwork; slot: MockupSlot; style?: unknown }) => contentApi.generateMockupSlot(req),
    onError: (e: Error) => toast({ title: 'Slot generation failed', description: e.message, variant: 'destructive' }),
  })
}

/**
 * Commission a Journal article. The request returns once the work is queued, not once
 * the article exists — writing takes about a minute and outlives the request. The draft
 * appears in the queue when the agent finishes, or not at all if the quality gate
 * rejects it, which is a legitimate outcome rather than a failure.
 */
export function useGenerateBlogArticle() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (brief: BlogBrief) => contentApi.generateBlogArticle(brief),
    onSuccess: () => {
      toast({
        title: 'Writing the article',
        description: 'It takes about a minute. Refresh the queue to see the draft.',
      })
      // Give the agent a head start before the list refetches, so the common case
      // (curator waits a moment, hits Refresh) has something to show.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: [ITEMS_KEY] })
        qc.invalidateQueries({ queryKey: [STATS_KEY] })
      }, 75000)
    },
    onError: (e: Error) => toast({ title: 'Could not queue the article', description: e.message, variant: 'destructive' }),
  })
}

// ─── labels ──────────────────────────────────────────────
export const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  room_collection: 'Rooms',
  artwork_editorial: 'Artwork',
  artist_editorial: 'Artists',
  easel_artwork_editorial: 'Editorial',
  blog_editorial: 'Journal',
  mockup: 'Mockups',
  gallery_set: 'Sets',
  gallery_wall_set: 'Gallery Walls',
  shoppable_room: 'Shoppable Rooms',
  recommendations: 'Recommendations',
  hero_asset: 'Assets',
}

export const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
  draft: 'Draft',
  review: 'In Review',
  active: 'Active',
  seasonal: 'Seasonal',
  archived: 'Archived',
  rejected: 'Rejected',
}
